import { useMemo, useState } from 'react';
import { Badge, Box, Flex, ScrollArea, Text, TextField } from '@radix-ui/themes';
import { FolderTree, Search, TerminalSquare } from 'lucide-react';
import { ROOT_PROJECT, type CodexUsage, type Project, type Session } from '@mac-remote/protocol';
import { CodexSession } from '../components/CodexSession';

/**
 * Subsequence match, not substring: "afb" finds "askyo-frontend-builder". With 92
 * directories whose names share long prefixes, plain `includes` means typing most
 * of the name before the list narrows.
 */
function fuzzy(needle: string, haystack: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();
  if (h.includes(n)) return true;
  let i = 0;
  for (const ch of h) if (ch === n[i]) i++;
  return i === n.length;
}

export function Projects({
  projects,
  sessions,
  busy,
  usage,
  onOpen,
  onOpenLimits,
}: {
  projects: Project[];
  sessions: Session[];
  busy: Set<string>;
  usage: CodexUsage | null;
  onOpen: (name: string) => void;
  onOpenLimits: () => void;
}) {
  const [q, setQ] = useState('');
  const live = useMemo(() => new Set(sessions.map((s) => s.project)), [sessions]);

  const shown = useMemo(() => {
    // Match and sort on what is written on the row: the root's `name` is the sentinel
    // `__root__`, so searching its visible name would otherwise never find it.
    const filtered = projects.filter((p) => fuzzy(q, p.label ?? p.name));
    return filtered.sort((a, b) => {
      const ab = busy.has(a.name), bb = busy.has(b.name);
      if (ab !== bb) return ab ? -1 : 1;
      const al = live.has(a.name), bl = live.has(b.name);
      if (al !== bl) return al ? -1 : 1;
      // Root pinned to the top while browsing — its mtime barely moves, so by date it
      // would sit among directories last touched months ago. Once you are searching,
      // relevance wins and it takes its chances like everything else.
      if (!q && (a.name === ROOT_PROJECT || b.name === ROOT_PROJECT)) {
        return a.name === ROOT_PROJECT ? -1 : 1;
      }
      // Exact prefix beats a scattered subsequence match.
      const ap = (a.label ?? a.name).toLowerCase().startsWith(q.toLowerCase());
      const bp = (b.label ?? b.name).toLowerCase().startsWith(q.toLowerCase());
      if (q && ap !== bp) return ap ? -1 : 1;
      return (b.lastUsedAt ?? 0) - (a.lastUsedAt ?? 0);
    });
  }, [projects, q, live, busy]);

  return (
    <Flex direction="column" style={{ height: '100%', minHeight: 0 }}>
      {/* Above the search, because it is the thing you check before deciding to start
          something, not something you go looking for. */}
      <Box px="2" pt="2">
        <CodexSession usage={usage} onClick={onOpenLimits} />
      </Box>

      <Box p="2">
        <TextField.Root
          placeholder="Поиск по проектам"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="3"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          style={{ fontSize: 16 }}
        >
          <TextField.Slot>
            <Search size={16} />
          </TextField.Slot>
        </TextField.Root>
      </Box>

      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Flex direction="column" gap="1" p="2" pt="0">
          {shown.length === 0 && (
            <Text size="2" align="center" style={{ color: 'var(--app-hint)', padding: 24 }}>
              {projects.length === 0 ? 'Жду список от мака…' : 'Ничего не нашлось'}
            </Text>
          )}
          {shown.map((p) => {
            const root = p.name === ROOT_PROJECT;
            const Icon = root ? FolderTree : TerminalSquare;
            return (
            <button key={p.name} className="project-row" onClick={() => onOpen(p.name)}>
              <Icon size={16} style={{ color: 'var(--app-hint)', flex: '0 0 auto' }} />
              <Text size="2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.label ?? p.name}
                {root && (
                  // Says which of two similar-looking rows you are about to open: the
                  // folder that holds everything, not a project inside it.
                  <Text size="1" style={{ color: 'var(--app-hint)' }}> · вся папка</Text>
                )}
              </Text>
              {busy.has(p.name) ? (
                <Badge color="jade" variant="solid" size="1">
                  работает
                </Badge>
              ) : (
                live.has(p.name) && (
                  <Badge color="gray" variant="soft" size="1">
                    сессия
                  </Badge>
                )
              )}
            </button>
            );
          })}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
